import * as React from "react";
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  makeStyles,
  Field,
  Text,
  Input,
  Divider,
  Textarea,
  Switch,
  Checkbox,
  InfoLabel,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Spinner,
  Dropdown,
  Option,
  Label
} from "@fluentui/react-components";
import { Dismiss24Regular, BinRecycleFullFilled } from "@fluentui/react-icons";
import { useToolbarContext } from "../contexts/ToolbarContext";
import NewDropzone from "../room/newDropzone";
import { ARTWORK_TYPES, artworkFields, useUploadFormContext } from "../contexts/UploadFormContext";
import ArtworkDropdown from "../fluent/ArtworkDropdown";
import { FluentNumericInput } from "../fluent/NumericInputCompound";
import useUrlUpload from "../hooks/useUrlUpload";
import { getMediaType } from "../../utils/custom-utils";
import configs from "../../utils/configs";
import { getAuthHeaders } from "../../utils/getToken";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { LogMessageType } from "../../utils/messages";
import useBreakpoints from "../hooks/useBreakpoints";
import { isThreeMeshUIValidInput, isYearValid } from "../../utils/three-mesh-ui-utils";
import { getMediaTypeFromUploadEntry, proxiedUrlFor } from "../../utils/media-url-utils";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";
import { useSidebarContext } from "../SidebarContext";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { Optimize3DModelDialog } from "../room/Optimize3DModelDialog";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { normalizeWorkspaceAssetUpdate } from "../../utils/workspace-asset-registration";
import { isWorkspaceSelectable } from "../contexts/workspace-selectable-utils";
import { Modal } from "../modal/Modal";
import { ResponsiveOverlayDrawer } from "../drawer/ResponsiveOverlayDrawer";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "var(--primaryColorBackground)"
  },
  compoundNumericInput: {
    minWidth: "60px",
    backgroundColor: "var(--secondaryColorBackground)"
  },
  drawerBody: {
    paddingTop: "20px",
    paddingBottom: "40px"
  },
  search: {
    backgroundColor: "var(--secondaryColorBackground)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--color-text-secondary)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  divider2: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  footer: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "var(--primaryColorBackground)"
  },
  redIcon: {
    color: "var(--color-danger)"
  },
  avatarGroupItem: {
    backgroundColor: "var(--secondaryColorBackground)",
    "& img": {
      backgroundColor: "var(--secondaryColorBackground)"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "var(--secondaryColorBackground)"
    }
  },
  textArtwork: {
    color: "var(--color-text-primary)",
    fontSize: "25px",
    fontWeight: "600"
  },
  input: {
    backgroundColor: "var(--secondaryColorBackground)",
    "& select": {
      backgroundColor: "var(--secondaryColorBackground)"
    },
    "& option": {
      backgroundColor: "var(--secondaryColorBackground)"
    }
  },
  thumbnail: {
    position: "relative",
    maxWidth: "100%",
    margin: "0 auto",
    "& .remove": {
      position: "absolute",
      top: "0",
      right: "0",
      cursor: "pointer",
      width: "30px",
      height: "30px",
      backgroundColor: "var(--accent1-color-hover)",
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: "3"
    }
  },
  accordionHeaderItem: {
    "& button": {
      paddingLeft: "0px"
    }
  }
});

const parseDimensionInches = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = String(value).trim();
  if (trimmed === "") return null;

  const inchesPattern = /^([\d.]+)\s*(?:in|inch|inches|")?$/i;
  const cmPattern = /^([\d.]+)\s*cm$/i;

  if (inchesPattern.test(trimmed)) {
    const match = trimmed.match(inchesPattern);
    const num = Number(match[1]);
    return Number.isFinite(num) ? num : null;
  }

  if (cmPattern.test(trimmed)) {
    const match = trimmed.match(cmPattern);
    const num = Number(match[1]);
    if (Number.isFinite(num)) {
      return +(num / 2.54);
    }
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildDimensionsText = (widthInches, heightInches, depthInches) => {
  const format = value => {
    if (!Number.isFinite(value)) return null;
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2).replace(/\.?0+$/, "");
  };

  const parts = [];
  const w = format(widthInches);
  const h = format(heightInches);
  const d = format(depthInches);

  if (w) parts.push(w);
  if (h) parts.push(h);
  if (d) parts.push(d);

  return parts.length > 0 ? `${parts.join(" x ")} in` : "";
};

export const FluentFileUpload = ({ performConditionalSignIn, store }) => {
  const {
    mediums,
    setMediums,
    setFrames,
    formSetValue,
    formWatch,
    onSubmitForm,
    handleSubmit,
    thumbnail,
    onChangeForm,
    handleFileLoadedDropzone,
    disabledSubmit,
    hasFile,
    onClose,
    loadingUpload,
    setThumbnail,
    url,
    setIndefiniteLoading,
    setErrorUpload,
    isEditMode,
    assetToEdit,
    populateFormFields
  } = useUploadFormContext();

  const {
    fileUploadOpen,
    onFileUploadOpen,
    isDigitalTwinArt,
    isDigitalArt,
    setFileUploadOpen,
    onDigitalArtChange,
    onDigitalTwinArtChange
  } = useToolbarContext();

  const { showOptimizeDialog, assetToOptimize, onOptimized, onOptimizeDialogClose } = useUploadFormContext();
  const { setSidebar } = useSidebarContext();
  const { workspaces, selectedWorkspace, setSelectedWorkspace } = useWorkspaceContext();
  const intl = useIntl();

  const { errorUpload, urlUpload, onErrorUrlUploadChange } = useUrlUpload();
  const [showUploadForbiddenModal, setShowUploadForbiddenModal] = React.useState(false);
  const requiredAssetPermission = isEditMode ? "asset:update" : "asset:create";

  // Ref for the NewDropzone component
  const dropzoneRef = React.useRef();

  // Store pending files to process once drawer is open
  const [pendingFiles, setPendingFiles] = React.useState(null);
  // Sort switchable workspaces: primaryOwner first, then is_default, then others
  const sortedWorkspaces = React.useMemo(() => {
    if (!workspaces || workspaces.length === 0) return [];

    const availableWorkspaces = workspaces.filter(
      workspace =>
        isWorkspaceSelectable(workspace) && workspace.authorization.permissions.includes(requiredAssetPermission)
    );

    const defaultWorkspaces = availableWorkspaces.filter(workspace => workspace.is_default);
    const otherWorkspaces = availableWorkspaces.filter(workspace => !workspace.is_default);

    return [...defaultWorkspaces, ...otherWorkspaces];
  }, [requiredAssetPermission, workspaces]);

  const effectiveWorkspace = React.useMemo(() => {
    if (
      selectedWorkspace &&
      isWorkspaceSelectable(selectedWorkspace) &&
      selectedWorkspace.authorization.permissions.includes(requiredAssetPermission)
    ) {
      return selectedWorkspace;
    }

    if (sortedWorkspaces.length > 0) {
      return sortedWorkspaces[0];
    }

    return selectedWorkspace;
  }, [requiredAssetPermission, selectedWorkspace, sortedWorkspaces]);
  const canCreateAssets = Boolean(
    effectiveWorkspace?.authorization?.projection_status === "ready" &&
      effectiveWorkspace.authorization.permissions?.includes("asset:create")
  );
  const canUpdateAssets = Boolean(
    effectiveWorkspace?.authorization?.projection_status === "ready" &&
      effectiveWorkspace.authorization.permissions?.includes("asset:update")
  );
  const canManageCurrentAsset = isEditMode ? canUpdateAssets : canCreateAssets;

  const [pendingUrl, setPendingUrl] = React.useState(null);

  const onUrlUpload = React.useCallback(async () => {
    if (!canCreateAssets) {
      setShowUploadForbiddenModal(true);
      setIndefiniteLoading(false);
      return;
    }

    setIndefiniteLoading(true);
    setErrorUpload(false);
    onErrorUrlUploadChange(null);
    try {
      const uploadData = await urlUpload(url, effectiveWorkspace);
      if (uploadData) {
        const newFile = [
          {
            downloableUrl: uploadData.url,
            uploadData
          }
        ];

        formSetValue(artworkFields.file, newFile);
        setThumbnail(uploadData.thumbnail);
        formSetValue(artworkFields.myMediaId, uploadData.id);

        // Derive a default title from the uploaded URL / metadata
        let fileName = uploadData?.title || uploadData?.name || (uploadData.url ? uploadData.url.split("/").pop() : "");
        // Remove query/hash fragments and extension if present
        if (fileName.includes("?")) fileName = fileName.split("?")[0];
        if (fileName.includes("#")) fileName = fileName.split("#")[0];
        if (fileName.includes(".")) fileName = fileName.substring(0, fileName.lastIndexOf("."));

        if (fileName) {
          formSetValue(artworkFields.title, fileName);
          formSetValue(artworkFields.artName, fileName);
        }
        onErrorUrlUploadChange(null);
      }
      setIndefiniteLoading(false);
    } catch (error) {
      setIndefiniteLoading(false);
      if (error?.status === 403) {
        setShowUploadForbiddenModal(true);
      }
    }
  }, [
    canCreateAssets,
    url,
    urlUpload,
    formSetValue,
    setThumbnail,
    setIndefiniteLoading,
    setErrorUpload,
    onErrorUrlUploadChange,
    effectiveWorkspace
  ]);

  React.useEffect(() => {
    if (fileUploadOpen) {
      onErrorUrlUploadChange(null);
    }
  }, [fileUploadOpen, onErrorUrlUploadChange]);

  // Process pending files once drawer is open and dropzone is available
  React.useEffect(() => {
    if (fileUploadOpen && dropzoneRef.current) {
      if (pendingFiles && pendingFiles.length > 0) {
        dropzoneRef.current.processFiles(pendingFiles);
        setPendingFiles(null);
      }

      if (pendingUrl) {
        onChangeForm({ target: { value: pendingUrl } }, artworkFields.url);
        setPendingUrl(null);
        // Trigger URL upload after a short delay to ensure the form is updated
        setTimeout(() => {
          onUrlUpload();
        }, 100);
      }
    }
  }, [fileUploadOpen, pendingFiles, pendingUrl, onChangeForm, onUrlUpload]);

  // Listen for scene events to open file upload with files or URLs
  React.useEffect(() => {
    const handleOpenFileUploadWithFiles = event => {
      const { files } = event.detail;
      if (files && files.length > 0) {
        // Store files to process once drawer is open
        setPendingFiles(files);
        // Open the file upload drawer
        onFileUploadOpen(true);
      }
    };

    const handleOpenFileUploadWithUrl = event => {
      const { url } = event.detail;
      if (url) {
        // Store URL to process once drawer is open
        setPendingUrl(url);
        // Open the file upload drawer
        onFileUploadOpen(true);
      }
    };

    // Listen for scene events
    if (window.APP && window.APP.scene) {
      window.APP.scene.addEventListener("open_file_upload_with_files", handleOpenFileUploadWithFiles);
      window.APP.scene.addEventListener("open_file_upload_with_url", handleOpenFileUploadWithUrl);
    }

    return () => {
      if (window.APP && window.APP.scene) {
        window.APP.scene.removeEventListener("open_file_upload_with_files", handleOpenFileUploadWithFiles);
        window.APP.scene.removeEventListener("open_file_upload_with_url", handleOpenFileUploadWithUrl);
      }
    };
  }, [onFileUploadOpen]);

  // Guard: if user opens the upload drawer while not signed-in, trigger conditional sign-in first.
  React.useEffect(() => {
    if (!fileUploadOpen || !performConditionalSignIn) return;

    const isSignedIn = () => !!(store.state.credentials.token || store.state.authUser.id);

    if (isSignedIn()) return; // nothing to do

    // Close drawer so it does not appear under modal
    setFileUploadOpen(false);

    performConditionalSignIn(isSignedIn, () => setFileUploadOpen(true));
  }, [fileUploadOpen, performConditionalSignIn, setFileUploadOpen]);

  React.useEffect(() => {
    if (!fileUploadOpen || canManageCurrentAsset) return;

    setFileUploadOpen(false);
    setShowUploadForbiddenModal(true);
  }, [fileUploadOpen, canManageCurrentAsset, setFileUploadOpen]);

  const styles = useStyles();
  const isReadOnlyMetadataEdit = isEditMode && assetToEdit?.metadata?.editable === false;

  const optionMediums = mediums?.length
    ? mediums.map(medium => ({ value: medium.model, label: medium.name, ...medium }))
    : [];

  React.useEffect(() => {
    setMediums();
    setFrames();
  }, []);

  const { unit } = artworkFields;

  React.useEffect(() => {
    if (isDigitalTwinArt) {
      formSetValue(unit, "in");
    } else if (isDigitalArt) {
      formSetValue(unit, "custom");
    } else {
      formSetValue(unit, "");
    }

    formSetValue(artworkFields.type, isDigitalTwinArt ? ARTWORK_TYPES.DIGITAL_TWIN_ART : ARTWORK_TYPES.DIGITAL_ART);
  }, [isDigitalTwinArt, isDigitalArt, formSetValue, unit]);

  // Form pre-population for edit mode
  React.useEffect(() => {
    if (assetToEdit && isEditMode) {
      // Set category flags based on asset category FIRST
      if (assetToEdit.asset_category === "digital-art") {
        onDigitalArtChange(true);
        onDigitalTwinArtChange(false);
      } else if (assetToEdit.asset_category === "digital-twin-art") {
        onDigitalArtChange(false);
        onDigitalTwinArtChange(true);
      } else {
        onDigitalArtChange(false);
        onDigitalTwinArtChange(false);
      }

      // Populate form fields AFTER category flags are set
      // Use setTimeout to ensure category flags are processed first
      setTimeout(() => {
        populateFormFields(assetToEdit);
      }, 0);
    }
  }, [assetToEdit, isEditMode, onDigitalArtChange, onDigitalTwinArtChange, populateFormFields]);

  // Cleanup edit state on component unmount
  React.useEffect(() => {
    return () => {
      if (isEditMode) {
        onClose();
      }
    };
  }, []);

  const buttonIcon = loadingUpload ? <Spinner size={"tiny"} /> : undefined;

  const onClearFile = () => {
    formSetValue(artworkFields.file, null);
    setThumbnail(null);
    onErrorUrlUploadChange(null);
  };

  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  const overlayStyle = isMobile ? {} : { width: "448px" };

  // Workspace options for dropdown
  const workspaceOptions = React.useMemo(() => {
    return sortedWorkspaces.map(workspace => ({
      key: workspace.id,
      text: workspace.name || workspace.id
    }));
  }, [sortedWorkspaces]);

  const handleWorkspaceChange = React.useCallback(
    (_, data) => {
      const workspaceId = data.optionValue;
      const workspace = sortedWorkspaces.find(w => w.id === workspaceId);
      if (workspace) {
        setSelectedWorkspace(workspace);
      }
    },
    [sortedWorkspaces, setSelectedWorkspace]
  );

  // Category options for edit mode dropdown
  const categoryOptions = [
    { key: "file", text: "File" },
    { key: "digital-art", text: "Digital Art" },
    { key: "digital-twin-art", text: "Digital Twin Art" }
  ];

  // Track selected category in local state
  const [selectedCategory, setSelectedCategory] = React.useState("file");

  // Sync selected category with assetToEdit when in edit mode
  React.useEffect(() => {
    if (isEditMode && assetToEdit) {
      setSelectedCategory(assetToEdit.asset_category || "file");
    } else if (!isEditMode) {
      // Reset to default when not in edit mode
      setSelectedCategory("file");
    }
  }, [isEditMode, assetToEdit]);

  // Handle category change in edit mode
  const handleCategoryChange = (_, data) => {
    const newCategory = data.optionValue;
    const oldCategory = selectedCategory;

    // Preserve title and description when switching categories
    // Get current values from the form
    const titleFromFile = formWatch(artworkFields.title);
    const titleFromArtwork = formWatch(artworkFields.artName);
    const currentTitle = titleFromFile || titleFromArtwork || "";
    const currentDescription = formWatch(artworkFields.description) || "";

    setSelectedCategory(newCategory);

    if (newCategory === "digital-art") {
      onDigitalArtChange(true);
      onDigitalTwinArtChange(false);
    } else if (newCategory === "digital-twin-art") {
      onDigitalArtChange(false);
      onDigitalTwinArtChange(true);
    } else {
      onDigitalArtChange(false);
      onDigitalTwinArtChange(false);
    }

    // Sync title/name between categories after state updates
    setTimeout(() => {
      if (newCategory === "file" && (oldCategory === "digital-art" || oldCategory === "digital-twin-art")) {
        // Switching from artwork to file: artName → title
        formSetValue(artworkFields.title, currentTitle, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        formSetValue(artworkFields.description, currentDescription, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      } else if ((newCategory === "digital-art" || newCategory === "digital-twin-art") && oldCategory === "file") {
        // Switching from file to artwork: title → artName
        formSetValue(artworkFields.artName, currentTitle, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        formSetValue(artworkFields.description, currentDescription, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      } else if (
        (oldCategory === "digital-art" || oldCategory === "digital-twin-art") &&
        (newCategory === "digital-art" || newCategory === "digital-twin-art")
      ) {
        // Switching between artwork types: preserve artName
        formSetValue(artworkFields.artName, currentTitle, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        formSetValue(artworkFields.description, currentDescription, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }, 0);
  };

  // Edit mode submit handler
  const onSubmitEdit = React.useCallback(async () => {
    if (!assetToEdit) return;

    try {
      // Gather current form data
      const updatedData = {};

      // Preserve existing metadata to avoid overwriting unmanaged fields
      const existingMetadata = assetToEdit.metadata || {};

      // For all categories, include basic fields
      if (selectedCategory === "file") {
        // File category - simple update
        const fileTitle = formWatch(artworkFields.title);
        const fileDescription = formWatch(artworkFields.description);

        updatedData.title = fileTitle;
        updatedData.description = fileDescription;
        updatedData.asset_category = "file";

        // When changing to "file", ALWAYS send metadata without artwork field
        // This ensures backend removes artwork metadata even if no other metadata exists
        const { artwork, ...otherMetadata } = existingMetadata || {};

        // Always set metadata, even if empty, to ensure backend removes artwork
        updatedData.metadata = otherMetadata;
      } else {
        // Digital art categories - merge with existing metadata
        const artName = formWatch(artworkFields.artName);
        const artworkDate = formWatch(artworkFields.artworkDate);
        const mediumValue = formWatch(artworkFields.medium);
        const frameValue = formWatch(artworkFields.frame);
        const dimensionsX = formWatch(artworkFields.dimensionsX);
        const dimensionsY = formWatch(artworkFields.dimensionsY);
        const dimensionsZ = formWatch(artworkFields.dimensionsZ);
        const artistName = formWatch(artworkFields.artistName);
        const artistBio = formWatch(artworkFields.artistBio);
        const artistStatement = formWatch(artworkFields.artistStatement);
        const declareRights = formWatch(artworkFields.declareRights);
        const AIcreation = formWatch(artworkFields.AIcreation);
        const aiToolUsed = formWatch(artworkFields.aiToolUsed);
        const techniqueUsed = formWatch(artworkFields.techniqueUsed);
        const currentPhysicalWork = formWatch(artworkFields.currentPhysicalWork);
        const customMedium = formWatch(artworkFields.customMedium);
        const authorDate = formWatch(artworkFields.authorDate);

        updatedData.title = artName;
        updatedData.description = formWatch(artworkFields.description);
        updatedData.asset_category = selectedCategory;

        // Preserve existing metadata and merge our changes
        const existingArtwork = existingMetadata.artwork || {};
        const existingArtworkMetadata = existingArtwork.metadata || {};

        const normalizedWidth = parseDimensionInches(dimensionsX);
        const normalizedHeight = parseDimensionInches(dimensionsY);
        const normalizedDepth = parseDimensionInches(dimensionsZ);
        const normalizedDimensionsText = buildDimensionsText(normalizedWidth, normalizedHeight, normalizedDepth);

        // Handle medium: preserve object if unchanged, otherwise use string or find object
        let mediumToSave = mediumValue;
        const mediumLabel = typeof mediumValue === "string" ? mediumValue : mediumValue?.label || mediumValue?.name;

        // Get existing medium label for comparison
        const existingMediumLabel =
          typeof existingArtwork.medium === "string"
            ? existingArtwork.medium
            : existingArtwork.medium?.name || existingArtwork.medium?.label;

        // Normalize empty values for comparison (treat "", undefined, null as equivalent)
        const normalizedMediumLabel = mediumLabel || undefined;
        const normalizedExistingMediumLabel = existingMediumLabel || undefined;

        // Determine what to save for artwork.medium
        if (normalizedMediumLabel === normalizedExistingMediumLabel && existingArtwork.medium) {
          // No change detected and there's an existing value → preserve original
          mediumToSave = existingArtwork.medium;
        } else if (mediumValue) {
          // There's a new value (changed or added for first time)
          if (typeof mediumValue === "string") {
            mediumToSave = mediumValue;
          } else if (mediumValue?.label || mediumValue?.name) {
            mediumToSave = mediumValue.label || mediumValue.name;
          }
        }

        // Handle frame: same logic as medium
        const frameLabel = typeof frameValue === "string" ? frameValue : frameValue?.label || frameValue?.name;

        const existingFrameLabel =
          typeof existingArtworkMetadata.frame === "string"
            ? existingArtworkMetadata.frame
            : existingArtworkMetadata.frame?.name || existingArtworkMetadata.frame?.label;

        // Normalize for comparison
        const normalizedFrameLabel = frameLabel || undefined;
        const normalizedExistingFrameLabel = existingFrameLabel || undefined;

        // Determine what to save for metadata.medium and metadata.frame
        let metadataMediumToSave = existingArtworkMetadata.medium;
        let metadataFrameToSave = existingArtworkMetadata.frame;

        // Update metadata.medium if changed or newly added
        if (normalizedMediumLabel !== normalizedExistingMediumLabel || !existingArtworkMetadata.medium) {
          if (mediumValue && typeof mediumValue === "object") {
            metadataMediumToSave = mediumValue;
          } else if (mediumValue) {
            metadataMediumToSave = normalizedMediumLabel;
          }
        }

        // Update metadata.frame if changed or newly added
        if (normalizedFrameLabel !== normalizedExistingFrameLabel || !existingArtworkMetadata.frame) {
          if (frameValue && typeof frameValue === "object") {
            metadataFrameToSave = frameValue;
          } else if (frameValue) {
            metadataFrameToSave = normalizedFrameLabel;
          }
        }

        // Build artwork metadata based on category
        const artworkMetadataToSave = {
          ...existingArtworkMetadata, // Preserve existing nested metadata
          // Common fields for both digital-art and digital-twin-art
          artistBio,
          declareRights,
          terms: formWatch(artworkFields.terms),
          workCreatedByAI: AIcreation,
          aiToolUsed,
          customMedium,
          authorDate,
          type: selectedCategory === "digital-twin-art" ? ARTWORK_TYPES.DIGITAL_TWIN_ART : ARTWORK_TYPES.DIGITAL_ART,
          asset_category: selectedCategory
        };

        // Category-specific fields
        if (selectedCategory === "digital-twin-art") {
          // Digital Twin Art: include ALL physical artwork fields
          artworkMetadataToSave.techniqueUsed = techniqueUsed;
          artworkMetadataToSave.currentPhysicalWork = currentPhysicalWork;
          artworkMetadataToSave.medium = metadataMediumToSave;
          artworkMetadataToSave.frame = metadataFrameToSave;
        } else if (selectedCategory === "digital-art") {
          // Digital Art: include medium/frame but NOT digitization fields
          artworkMetadataToSave.medium = metadataMediumToSave;
          artworkMetadataToSave.frame = metadataFrameToSave;
          // Remove digitization-specific fields if they existed
          delete artworkMetadataToSave.techniqueUsed;
          delete artworkMetadataToSave.currentPhysicalWork;
        }

        // Build artwork object
        const artworkToSave = {
          ...existingArtwork, // Preserve existing artwork fields
          // Update only the fields we manage
          title: artName,
          artworkDate,
          author: artistName,
          artistStatement,
          description: formWatch(artworkFields.description),
          dimensionsX: normalizedWidth ?? existingArtwork.dimensionsX ?? "",
          dimensionsY: normalizedHeight ?? existingArtwork.dimensionsY ?? "",
          dimensionsZ: normalizedDepth ?? existingArtwork.dimensionsZ ?? "",
          dimensions: normalizedDimensionsText || existingArtwork.dimensions || "",
          asset_category: selectedCategory,
          metadata: artworkMetadataToSave
        };

        // Include medium in artwork root level for both digital-art and digital-twin-art
        if (selectedCategory === "digital-twin-art" || selectedCategory === "digital-art") {
          if (mediumToSave) {
            artworkToSave.medium = mediumToSave;
          }
        }

        updatedData.metadata = {
          ...existingMetadata, // Preserve any other metadata fields (not artwork-related)
          artwork: artworkToSave
        };
      }

      // Perform API update
      // Build assets URL with workspace for API update
      const baseUrl = configs.PERSISTENCE_API_URL;
      const assetsUrl = effectiveWorkspace?.id
        ? `${baseUrl}/workspaces/${effectiveWorkspace.id}/assets/${assetToEdit.id}`
        : `${baseUrl}/assets/${assetToEdit.id}`;
      const response = await fetch(assetsUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(await getAuthHeaders(assetsUrl, "PATCH"))
        },
        body: JSON.stringify(normalizeWorkspaceAssetUpdate(updatedData))
      });

      if (!response.ok) {
        const error = new Error("Failed to update asset");
        error.status = response.status;
        throw error;
      }

      // Emit event to update any scene entities using this asset
      if (typeof AFRAME !== "undefined" && AFRAME.scenes[0]) {
        AFRAME.scenes[0].emit("asset-metadata-updated", {
          assetId: assetToEdit.id,
          assetUuid: assetToEdit.uuid,
          updatedMetadata: updatedData
        });
      }

      notificationsCenter.success(LogMessageType.assetUpdated);
      onClose();

      // Reopen assets sidebar after successful edit
      setTimeout(() => {
        setSidebar("assets");
      }, 100);
    } catch (error) {
      if (error?.status === 403) {
        setShowUploadForbiddenModal(true);
        return;
      }
      console.error("Failed to update asset:", error);
      notificationsCenter.error(LogMessageType.assetUpdateFailed);
    }
  }, [assetToEdit, formWatch, selectedCategory, onClose, setSidebar]);

  const onFormSubmit = async data => {
    if (!canManageCurrentAsset) {
      setShowUploadForbiddenModal(true);
      return;
    }

    if (isEditMode) {
      await onSubmitEdit();
    } else {
      try {
        await onSubmitForm(data, { workspaceOverride: effectiveWorkspace });
      } catch (error) {
        if (error?.status === 403) {
          setShowUploadForbiddenModal(true);
        }
      }
    }
  };

  return (
    <>
      <ResponsiveOverlayDrawer
        size="full"
        desktopPosition="end"
        style={overlayStyle}
        modalType="non-modal"
        open={fileUploadOpen}
        onOpenChange={(_, { open }) => onFileUploadOpen(open)}
        as="form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <DrawerHeader className={styles.headerDrawer}>
          <DrawerHeaderTitle
            style={{ width: "100%" }}
            action={
              <Button
                appearance="subtle"
                aria-label={<FormattedMessage id="upload.close" defaultMessage="Close" />}
                icon={<Dismiss24Regular />}
                onClick={() => onClose()}
              />
            }
          >
            {isEditMode ? (
              <FormattedMessage id="upload.edit-title" defaultMessage="Edit Asset" />
            ) : (
              <FormattedMessage id="upload.title" defaultMessage="Upload" />
            )}
          </DrawerHeaderTitle>
        </DrawerHeader>
        {/* <form as="form" onSubmit={onSubmitForm}> */}
        <DrawerBody className={styles.drawerBody} id="upload-object-modal">
          {/* Workspace dropdown - only show when more than one workspace is available, placed on top */}
          {workspaceOptions.length > 1 && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
                <Label>
                  <FormattedMessage id="upload.workspace" defaultMessage="Workspace" />
                </Label>
                <Dropdown
                  appearance="filled-darker"
                  selectedOptions={effectiveWorkspace?.id ? [effectiveWorkspace.id] : []}
                  value={effectiveWorkspace?.name || ""}
                  onOptionSelect={handleWorkspaceChange}
                  disabled={isReadOnlyMetadataEdit}
                >
                  {workspaceOptions.map(option => (
                    <Option key={option.key} value={option.key}>
                      {option.text}
                    </Option>
                  ))}
                </Dropdown>
              </div>
              <Divider className={styles.divider} />
            </>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {!isEditMode && !thumbnail && (
              <NewDropzone
                ref={dropzoneRef}
                thumbnail={thumbnail}
                onFileLoaded={handleFileLoadedDropzone}
              ></NewDropzone>
            )}
            {((isEditMode && assetToEdit) || thumbnail) && (
              <ThumbnailComponent
                onClear={isEditMode ? null : onClearFile}
                styles={styles}
                thumbnail={isEditMode ? assetToEdit?.thumbnail : thumbnail}
              />
            )}

            {!isEditMode && !hasFile && (
              <>
                <Text color="var(--color-text-primary)" style={{ textTransform: "uppercase" }} align="center">
                  {" "}
                  OR{" "}
                </Text>
                <Field
                  validationMessage={errorUpload ? errorUpload : undefined}
                  label={{
                    children: (_, props) => (
                      <InfoLabel {...props} info="Upload files via URL">
                        URL
                      </InfoLabel>
                    )
                  }}
                >
                  <Input
                    type="text"
                    placeholder="Add URL"
                    onChange={e => {
                      onChangeForm(e, artworkFields.url);
                    }}
                    appearance="filled-lighter"
                    className={styles.input}
                    onBlur={e => onUrlUpload()}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        onUrlUpload();
                      }
                    }}
                  />
                </Field>
              </>
            )}
          </div>

          {/* Category dropdown for edit mode - only for images and models */}
          {isEditMode && assetToEdit && (assetToEdit.type === "image" || assetToEdit.type === "model") && (
            <>
              <Divider className={styles.divider} />
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Label>
                  <FormattedMessage id="upload.asset-category" defaultMessage="Asset Category" />
                </Label>
                <Dropdown
                  appearance="filled-darker"
                  selectedOptions={[selectedCategory]}
                  value={categoryOptions.find(opt => opt.key === selectedCategory)?.text}
                  onOptionSelect={handleCategoryChange}
                  disabled={isReadOnlyMetadataEdit}
                >
                  {categoryOptions.map(option => (
                    <Option key={option.key} value={option.key}>
                      {option.text}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            </>
          )}

          {(isDigitalArt || isDigitalTwinArt) && (
            <>
              <Divider className={styles.divider} />
              <WorkInformation
                mediums={optionMediums}
                styles={styles}
                isEditMode={isEditMode}
                assetToEdit={assetToEdit}
                disabled={isReadOnlyMetadataEdit}
              />
              <Divider className={styles.divider2} />
              <ArtistInformation styles={styles} disabled={isReadOnlyMetadataEdit} />
              <Divider className={styles.divider2} />
              <LegalInformation styles={styles} disabled={isReadOnlyMetadataEdit} />
              <Divider className={styles.divider2} />
              <AICreationIndification styles={styles} disabled={isReadOnlyMetadataEdit} />
              <Divider className={styles.divider2} />
              <Checkbox
                checked={formWatch(artworkFields.terms) || false}
                onChange={e => onChangeForm(e, artworkFields.terms, "toggle")}
                label={"I accept the terms and conditions"}
                required
                disabled={isReadOnlyMetadataEdit}
              />
            </>
          )}

          {!isDigitalArt && !isDigitalTwinArt && (
            <>
              <Divider className={styles.divider} />
              <GeneralTitleDescription styles={styles} disabled={isReadOnlyMetadataEdit} />
              {!isEditMode && (
                <>
                  <Divider className={styles.divider} />
                  <Accordion collapsible multiple>
                    <AccordionItem value="1">
                      <AccordionHeader className={styles.accordionHeaderItem}>
                        <Text size={200} style={{ lineHeight: "20px" }}>
                          Advanced Options
                        </Text>
                      </AccordionHeader>
                      <AccordionPanel style={{ display: "flex", flexDirection: "column", gap: "20px", margin: "0" }}>
                        <Field
                          label={{
                            children: (_, props) => (
                              <InfoLabel {...props} info="Redirect users to another location">
                                URL
                              </InfoLabel>
                            )
                          }}
                        >
                          <Input
                            onChange={e => onChangeForm(e, "href")}
                            type="text"
                            placeholder="Add URL"
                            appearance="filled-lighter"
                            className={styles.input}
                          />
                        </Field>

                        <Field
                          label={{
                            children: (_, props) => (
                              <InfoLabel {...props} info="Use a button on the object to navigate to the URL.">
                                Hide Open Link Button
                              </InfoLabel>
                            )
                          }}
                        >
                          <Switch
                            label={`Hide Open Link button for the Custom URL and make the object fully clickable`}
                            labelPosition="before"
                            onChange={e => onChangeForm(e, "hideHref", "toggle")}
                          />
                        </Field>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </>
              )}
            </>
          )}
        </DrawerBody>
        <div className={styles.footer} style={{ justifyContent: "flex-end" }}>
          <Button
            icon={buttonIcon}
            appearance="primary"
            type="submit"
            disabled={isReadOnlyMetadataEdit ? !canManageCurrentAsset || loadingUpload : disabledSubmit}
          >
            {isEditMode ? (
              <FormattedMessage id="upload.update-button" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="upload.submit" defaultMessage="Upload" />
            )}
          </Button>
        </div>
        {/* </form> */}
      </ResponsiveOverlayDrawer>

      {/* Optimization Dialog for 3D Models */}
      {showOptimizeDialog && assetToOptimize && (
        <Optimize3DModelDialog
          asset={assetToOptimize}
          open={showOptimizeDialog}
          onOptimized={onOptimized}
          onClose={onOptimizeDialogClose}
          isFromUpload={true}
        />
      )}
      {showUploadForbiddenModal && (
        <Modal
          open={showUploadForbiddenModal}
          onOpenChange={(_, data) => {
            if (!data.open) {
              setShowUploadForbiddenModal(false);
            }
          }}
          actions={
            <Button appearance="primary" onClick={() => setShowUploadForbiddenModal(false)}>
              <FormattedMessage id="upload.permission.understand" defaultMessage="I Understand" />
            </Button>
          }
        >
          <Text>
            <FormattedMessage
              id="upload.permission.message"
              defaultMessage="You're not allowed to upload files to this workspace."
            />
          </Text>
        </Modal>
      )}
    </>
  );
};

// propTypes for new prop
FluentFileUpload.propTypes = {
  performConditionalSignIn: PropTypes.func,
  store: PropTypes.object
};

function ThumbnailComponent({ thumbnail, onClear, styles }) {
  const mediaType = getMediaType(thumbnail);

  const exceptions = ["audio", "video"];
  return (
    <div className={styles.thumbnail}>
      <div className="remove" onClick={onClear}>
        <BinRecycleFullFilled style={{ width: "20px", height: "20px" }} />
      </div>
      {mediaType === "audio" && <audio style={{ padding: "4px", marginRight: "23px" }} src={thumbnail} controls />}
      {mediaType === "video" && <video src={proxiedUrlFor(thumbnail)} controls />}
      {!exceptions.includes(mediaType) && <img src={thumbnail}></img>}
    </div>
  );
}

function GeneralTitleDescription({ styles, disabled = false }) {
  const { onChangeForm, formWatch } = useUploadFormContext();
  const [titleError, setTitleError] = React.useState(false);
  const [descriptionError, setDescriptionError] = React.useState(false);

  const handleTitleChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.title);
    setTitleError(!isThreeMeshUIValidInput(value));
  };

  const handleDescriptionChange = e => {
    const value = e.target.value;
    onChangeForm(e, "description");
    setDescriptionError(!isThreeMeshUIValidInput(value));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Title"}
        required
        validationMessage={titleError ? "Some characters in the title are not valid" : undefined}
      >
        <Input
          disabled={disabled}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add title"
          value={formWatch(artworkFields.title) || ""}
          onChange={handleTitleChange}
        />
      </Field>
      <Field
        label={"Description"}
        validationMessage={descriptionError ? "Some characters in the description are not valid" : undefined}
      >
        <Textarea
          disabled={disabled}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Descriptive detail of the work..."
          value={formWatch(artworkFields.description) || ""}
          onChange={handleDescriptionChange}
        />
      </Field>

      {/* <Field>
        <Switch
          onChange={e => onChangeForm(e, artworkFields.enableARTrigger, "toggle")}
          label={{
            children: (_, props) => (
              <InfoLabel
                {...props}
                info="Lorem ipsum dolor sit amet consectetur. Consectetur quis egestas et maecenas nunc nulla viverra in."
              >
                Enable AR Trigger
              </InfoLabel>
            )
          }}
        />
      </Field> */}
    </div>
  );
}

function WorkInformation({ styles, mediums, isEditMode, assetToEdit, disabled = false }) {
  const { isDigitalTwinArt, isDigitalArt } = useToolbarContext();
  const title = isDigitalTwinArt ? "Digital Twin Art" : "Digital Art";
  const infoLabelText = isDigitalTwinArt
    ? "Create a digital twin of your physical artwork. Provide details about your physical piece to create a digital art label and a QR code, allowing users to view a digital version of your work."
    : "Upload your digital artwork. This option generates an art label and a QR code, allowing you to share your digital pieces with the community.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <InfoLabel size="large" info={infoLabelText}>
        <Text className={styles.textArtwork}>{title} </Text>
      </InfoLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Work Information
        </Text>
        <WorkInformationContent
          styles={styles}
          mediums={mediums}
          isDigitalArt={isDigitalArt}
          isDigitalTwinArt={isDigitalTwinArt}
          disabled={disabled}
          isEditMode={isEditMode}
          assetToEdit={assetToEdit}
        />
      </div>
    </div>
  );
}

export function WorkInformationContent({
  styles,
  mediums,
  isDigitalTwinArt,
  isDigitalArt,
  defaultValues = {},
  disabled = false,
  isPublicDomainArt,
  isEditMode = false,
  assetToEdit = null
}) {
  const { digitalizationSelect, currentStateSelect, onChangeForm, frames, file, url } = useUploadFormContext();
  const { selectedObject } = useObjectSettings();

  const canEditMediumAndFrame = disabled && isEditMode && assetToEdit?.metadata?.editable === false;
  const disableMediumAndFrame = disabled && !canEditMediumAndFrame;

  const optionFrames = frames?.length ? frames.map(frame => ({ value: frame.model, label: frame.name, ...frame })) : [];

  const [selectedMedium, setSelectedMedium] = React.useState({ label: defaultValues[artworkFields.medium] || "--" });
  const [selectedFrame, setSelectedFrame] = React.useState({ label: defaultValues[artworkFields.frame] || "--" });
  const [selectedTechnique, setSelectedTechnique] = React.useState({
    label: defaultValues[artworkFields.techniqueUsed] || "--"
  });
  const [selectedCurrentState, setSelectedCurrentState] = React.useState({
    label: defaultValues[artworkFields.currentPhysicalWork] || "--"
  });

  const handleMediumChange = (value, option) => {
    setSelectedMedium(option);
    onChangeForm(option, "medium", "value", option);
  };

  const handleFrameChange = (value, option) => {
    setSelectedFrame(option);
    onChangeForm(option, artworkFields.frame, "value", option);
  };

  const handleTechniqueChange = (value, option) => {
    setSelectedTechnique(option);
    onChangeForm(option, artworkFields.techniqueUsed, "value", value);
  };

  const handleCurrentStateChange = (value, option) => {
    setSelectedCurrentState(option);
    onChangeForm(option, artworkFields.currentPhysicalWork, "value", value);
  };

  const [titleError, setTitleError] = React.useState(false);
  const [descriptionError, setDescriptionError] = React.useState(false);
  const [dateError, setDateError] = React.useState(false);
  const [customMediumError, setCustomMediumError] = React.useState(false);

  const handleTitleChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artName);
    setTitleError(!isThreeMeshUIValidInput(value));
  };

  const handleDescriptionChange = e => {
    const value = e.target.value;
    onChangeForm(e, "description");
    setDescriptionError(!isThreeMeshUIValidInput(value));
  };

  const handleDateChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artworkDate);
    setDateError(!isYearValid(value));
  };

  React.useEffect(() => {
    if (defaultValues[artworkFields.artworkDate]) {
      const dateError = !isYearValid(defaultValues[artworkFields.artworkDate]);
      setDateError(dateError);
    }
  }, [artworkFields.artworkDate]);

  const handleCustomMediumChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.customMedium);
    setCustomMediumError(!isThreeMeshUIValidInput(value));
  };

  // Determine media type: in edit mode use assetToEdit.type, otherwise calculate from current file/object
  const mediaType =
    isEditMode && assetToEdit
      ? assetToEdit.type
      : selectedObject
      ? selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.imageTexture
        ? "image"
        : getMediaType(selectedObject?.el?.components["media-loader"]?.data?.src)
      : file && file.length
      ? getMediaTypeFromUploadEntry(file[0], url)
      : null;

  React.useEffect(() => {}, []);

  const { formWatch } = useUploadFormContext();

  // Sync local state with form values for edit mode
  const formMedium = formWatch(artworkFields.medium);
  const formFrame = formWatch(artworkFields.frame);
  const formTechnique = formWatch(artworkFields.techniqueUsed);
  const formCurrentState = formWatch(artworkFields.currentPhysicalWork);

  React.useEffect(() => {
    if (formMedium) {
      // Handle both string and object formats
      const mediumLabel = typeof formMedium === "string" ? formMedium : formMedium?.label || formMedium?.name;
      if (mediumLabel && mediumLabel !== selectedMedium.label) {
        const foundMedium = mediums.find(m => m.name === mediumLabel || m.label === mediumLabel);
        if (foundMedium) {
          setSelectedMedium({ label: foundMedium.name, value: foundMedium.model, ...foundMedium });
        } else if (mediumLabel !== "--") {
          setSelectedMedium({ label: mediumLabel });
        }
      }
    }
  }, [formMedium, mediums, selectedMedium.label]);

  React.useEffect(() => {
    if (formFrame) {
      // Handle both string and object formats
      const frameLabel = typeof formFrame === "string" ? formFrame : formFrame?.label || formFrame?.name;
      if (frameLabel && frameLabel !== selectedFrame.label) {
        const foundFrame = frames.find(f => f.name === frameLabel || f.label === frameLabel);
        if (foundFrame) {
          setSelectedFrame({ label: foundFrame.name, value: foundFrame.model, ...foundFrame });
        } else if (frameLabel !== "--") {
          setSelectedFrame({ label: frameLabel });
        }
      }
    }
  }, [formFrame, frames, selectedFrame.label]);

  React.useEffect(() => {
    if (formTechnique && formTechnique !== selectedTechnique.label) {
      setSelectedTechnique({ label: formTechnique });
    }
  }, [formTechnique, selectedTechnique.label]);

  React.useEffect(() => {
    if (formCurrentState && formCurrentState !== selectedCurrentState.label) {
      setSelectedCurrentState({ label: formCurrentState });
    }
  }, [formCurrentState, selectedCurrentState.label]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Title"}
        required
        validationMessage={titleError ? "Some characters in the title are not valid" : undefined}
      >
        <Input
          disabled={disabled || isPublicDomainArt}
          value={formWatch(artworkFields.artName) || ""}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Add name"
          onChange={handleTitleChange}
        />
      </Field>
      <Field
        label={"Description"}
        validationMessage={descriptionError ? "Some characters in the description are not valid" : undefined}
      >
        <Textarea
          disabled={disabled || isPublicDomainArt}
          value={formWatch(artworkFields.description) || ""}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Descriptive detail of the work..."
          onChange={handleDescriptionChange}
        />
      </Field>
      <Field label={"Date"} required validationMessage={dateError ? "input must be a valid Year" : undefined}>
        <Input
          disabled={disabled || isPublicDomainArt}
          value={formWatch(artworkFields.artworkDate) || ""}
          appearance="filled-lighter"
          className={styles.input}
          placeholder="YYYY"
          onChange={handleDateChange}
        />
      </Field>
      {/* Medium and Frame are only for images, not for GLB models */}
      {mediaType && mediaType === "image" && (
        <>
          <Field label={"Medium"}>
            <ArtworkDropdown
              id={"medium"}
              searchByLabel={true}
              disabled={disableMediumAndFrame}
              value={selectedMedium.label}
              options={mediums}
              className={styles.input}
              placeholder="Select a Medium"
              onChange={handleMediumChange}
            />
          </Field>

          {selectedMedium.label === "Custom" && (
            <Field
              label={"Custom Medium"}
              validationMessage={customMediumError ? "Medium has an invalid character" : undefined}
            >
              <Input
                searchByLabel={true}
                appearance="filled-lighter"
                id={"custom Medium"}
                disabled={disableMediumAndFrame}
                value={formWatch(artworkFields.customMedium) || ""}
                className={styles.input}
                placeholder="Custom Medium"
                onChange={handleCustomMediumChange}
              />
            </Field>
          )}

          <Field label={"Frame"}>
            <ArtworkDropdown
              id={"frame"}
              value={selectedFrame.label}
              disabled={disableMediumAndFrame}
              options={optionFrames}
              className={styles.input}
              placeholder="Select a Frame"
              onChange={handleFrameChange}
            />
          </Field>
        </>
      )}
      {isDigitalTwinArt && (
        <Field
          label={{
            children: (_, props) => (
              <InfoLabel
                {...props}
                info="Artwork dimensions in inches typically refer to the physical size of the piece"
              >
                Dimensions
              </InfoLabel>
            )
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <FluentNumericInput
              value={formWatch(artworkFields.dimensionsY) || ""}
              disabled={disabled || isPublicDomainArt}
              min={0}
              placeholder={"Height (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsY", "value", value)}
            />
            <FluentNumericInput
              value={formWatch(artworkFields.dimensionsX) || ""}
              min={0}
              disabled={disabled || isPublicDomainArt}
              placeholder={"Width (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsX", "value", value)}
            />
            <FluentNumericInput
              value={formWatch(artworkFields.dimensionsZ) || ""}
              min={0}
              disabled={disabled || isPublicDomainArt}
              placeholder={"Depth (in)"}
              className={styles.compoundNumericInput}
              onChange={value => onChangeForm({}, "dimensionsZ", "value", value)}
            />
          </div>
        </Field>
      )}
      {isDigitalTwinArt && !isDigitalArt && (
        <>
          <Field
            label={{
              children: (_, props) => (
                <InfoLabel {...props} info="Method used to digitalize the artwork">
                  Digitization technique used
                </InfoLabel>
              )
            }}
          >
            <ArtworkDropdown
              id="technique"
              value={selectedTechnique.label}
              disabled={disabled || isPublicDomainArt}
              options={digitalizationSelect}
              className={styles.input}
              type="text"
              placeholder="Select the technique"
              onChange={handleTechniqueChange}
            />
          </Field>
          <Field
            label={{
              children: (_, props) => (
                <InfoLabel
                  {...props}
                  info="Select the current condition of the physical artwork from the dropdown menu."
                >
                  Current state of the physical work
                </InfoLabel>
              )
            }}
          >
            <ArtworkDropdown
              value={selectedCurrentState.label}
              disabled={disabled || isPublicDomainArt}
              options={currentStateSelect}
              className={styles.input}
              type="text"
              placeholder="Select status"
              onChange={handleCurrentStateChange}
            />
          </Field>
        </>
      )}

      {/* <Field>
            <Switch
              checked={enableARTrigger}
              // defaultValue={defaultValues[artworkFields.enableARTrigger]}
              onChange={e => handleSwitchChange(e)}
              label={{
                children: (_, props) => (
                  <InfoLabel
                    {...props}
                    info="Lorem ipsum dolor sit amet consectetur. Consectetur quis egestas et maecenas nunc nulla viverra in."
                  >
                    Enable AR Trigger
                  </InfoLabel>
                )
              }}
            />
          </Field> */}
    </div>
  );
}

function ArtistInformation({ styles, disabled = false }) {
  const { isDigitalTwinArt } = useToolbarContext();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Artist Information
        </Text>
        <ArtistInformationContent styles={styles} isDigitalTwinArt={isDigitalTwinArt} disabled={disabled} />
      </div>
    </div>
  );
}

export function ArtistInformationContent({ styles, isDigitalTwinArt, defaultValues = {}, disabled = false }) {
  const { onChangeForm, formWatch } = useUploadFormContext();
  const [artistError, setArtistError] = React.useState(false);
  const [bioError, setBioError] = React.useState(false);
  const [statementError, setStatementError] = React.useState(false);

  const handleArtistChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistName);
    setArtistError(!isThreeMeshUIValidInput(value));
  };

  const handleBioChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistBio);
    setBioError(!isThreeMeshUIValidInput(value));
  };

  const handleStatementChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.artistStatement);
    setStatementError(!isThreeMeshUIValidInput(value));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Field
        label={"Name"}
        required
        validationMessage={artistError ? "Some characters in the artist name are not valid" : undefined}
      >
        <Input
          disabled={disabled}
          value={formWatch(artworkFields.artistName) || ""}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Artist Name"
          onChange={handleArtistChange}
        />
      </Field>
      <Field label={"Bio"} validationMessage={bioError ? "Some characters in the bio are not valid" : undefined}>
        <Textarea
          disabled={disabled}
          value={formWatch(artworkFields.artistBio) || ""}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Brief biography of the artist..."
          onChange={handleBioChange}
        />
      </Field>
      <Field
        label={"Statement"}
        validationMessage={statementError ? "Some characters in the statement are not valid" : undefined}
      >
        <Textarea
          disabled={disabled}
          value={formWatch(artworkFields.artistStatement) || ""}
          rows={3}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Artist statement about his work, aspirations and goals..."
          onChange={handleStatementChange}
        />
      </Field>
      {isDigitalTwinArt && (
        <Field label={"Date"}>
          <Input
            disabled={disabled}
            value={formWatch(artworkFields.authorDate) || ""}
            appearance="filled-lighter"
            className={styles.input}
            type="date"
            placeholder="DD-MM-YYYY"
            onChange={e => onChangeForm(e, artworkFields.authorDate)}
          />
        </Field>
      )}
    </div>
  );
}

function LegalInformation({ styles, disabled = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          Legal and Rights Information
        </Text>
        <LegalInformationContent styles={styles} disabled={disabled} />
      </div>
    </div>
  );
}

export function LegalInformationContent({ styles, defaultValues = {}, disabled = false }) {
  const { onChangeForm, formWatch } = useUploadFormContext();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Checkbox
        disabled={disabled}
        checked={formWatch(artworkFields.declareRights) || false}
        onChange={e => onChangeForm(e, artworkFields.declareRights, "toggle")}
        label={"I declare that this work is original and I have the necessary rights for its publication."}
        required
      />
    </div>
  );
}

function AICreationIndification({ styles, disabled = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Text weight="semibold" style={{ fontSize: "18px" }}>
          AI Creation Indication
        </Text>
        <AICreationIndicationContent styles={styles} disabled={disabled} />
      </div>
    </div>
  );
}

export function AICreationIndicationContent({ styles, defaultValues = {}, disabled = false }) {
  const { onChangeForm, formWatch } = useUploadFormContext();

  const [toolError, setToolError] = React.useState(false);
  const aiCreationChecked = formWatch(artworkFields.AIcreation) || false;

  const handleToolChange = e => {
    const value = e.target.value;
    onChangeForm(e, artworkFields.aiToolUsed);
    setToolError(!isThreeMeshUIValidInput(value)); // Validate input
  };

  const handleCheckboxChange = e => {
    onChangeForm(e, artworkFields.AIcreation, "toggle");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Checkbox
        disabled={disabled}
        checked={aiCreationChecked}
        label={"This work was created in whole or in part by an AI"}
        onChange={e => handleCheckboxChange(e)}
      />
      <Field
        label={"What AI Tool Used?"}
        validationMessage={toolError ? "Some characters in the tool name are not valid" : undefined}
      >
        <Input
          disabled={disabled || !aiCreationChecked}
          value={formWatch(artworkFields.aiToolUsed) || ""}
          appearance="filled-lighter"
          className={styles.input}
          type="text"
          placeholder="Tool name"
          onChange={handleToolChange}
        />
      </Field>
    </div>
  );
}
